/**
 * The pieces of a day sheet both surfaces draw: the office review page
 * (SheetScreen) and the field sheet (field/WlFieldSheetScreen). Both read the
 * camelCase WlSheet (the field hooks camelize the snake_case wire once).
 *
 *   SheetWarnings   — the API's warnings as sentences. Never block anything.
 *   SheetSummary    — the API's totals: hours, footage, run count.
 *   SheetReadout    — crew and equipment, read only.
 *   SheetEditor     — the draft's header, crew and equipment, saved whole.
 *   SheetRuns       — the runs, each opening its own page.
 *
 * NOTHING IS COMPUTED HERE. Crew hours, equipment hours, footage (2 × max
 * depth) and the sheet window are the API's; the editor sends what was typed.
 */
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight, Plus, Trash2 } from 'lucide-react';
import { fmtWlNumber, wlToInput, WL_TIME_PATTERN } from '../../lib/wireline-slickline';
import { wlClockRange, wlHours, wlQty, wlWarningText } from '../../lib/wireline-slickline-jobs';
import type { WlCatalog } from '../../types/wireline-slickline';
import type { WlSheet, WlSheetCrewWrite, WlSheetEquipmentWrite, WlSheetWrite, WlWarningEntry } from '../../types/wireline-slickline-jobs';
import { WL_INPUT, WL_PRIMARY, WL_SMALL_BTN, WlEmpty, WlField, WlFormError, WlPanel, WlSpec, WlWarning } from './WlBits';

const FALLBACK_POSITIONS = [
  { value: 'supervisor', label: 'Supervisor' },
  { value: 'operator', label: 'Operator' },
  { value: 'helper', label: 'Helper' },
];

/** Every warning the API returned, as a sentence. `extra` is a write's own warnings (save / approve). */
export function SheetWarnings({ sheet, extra = [] }: { sheet: WlSheet; extra?: WlWarningEntry[] }) {
  const unitName = (id: string) => sheet.equipment.find((e) => e.equipmentId === id)?.unitName ?? null;
  const all = [...(sheet.warnings ?? []), ...extra];
  if (!all.length) return null;
  return (
    <div className="space-y-2" data-testid="wl-sheet-warnings">
      {all.map((w, i) => (
        <WlWarning key={`${w.code}-${i}`} message={wlWarningText(w, { unitName })} testId={`wl-warning-${w.code}`} />
      ))}
    </div>
  );
}

export function SheetSummary({ sheet }: { sheet: WlSheet }) {
  const t = sheet.totals;
  return (
    <WlPanel eyebrow="Day sheet" title="Summary" testId="wl-sheet-summary">
      <dl className="grid gap-4 px-4 py-4 sm:grid-cols-2 lg:grid-cols-4">
        <WlSpec label="Window" value={wlClockRange(sheet.startTime, sheet.endTime)} mono />
        <WlSpec label="Sheet hours" value={wlHours(sheet.sheetHours)} mono />
        <WlSpec label="Crew hours" value={wlHours(t?.crewHours)} mono />
        <WlSpec label="Runs" value={t?.runCount === null || t?.runCount === undefined ? '—' : fmtWlNumber(t.runCount)} mono />
        <WlSpec label="Footage" value={wlQty(t?.footage, 1)} mono />
        <WlSpec label="Owned unit hours" value={wlHours(t?.ownedEquipmentHours)} mono />
        <WlSpec label="Rent-in hours" value={wlHours(t?.rentInHours)} mono />
        <WlSpec label="Standby / travel" value={`${wlHours(sheet.standbyHours)} / ${wlHours(sheet.travelHours)}`} mono />
      </dl>
      {sheet.remarks && <p className="border-t border-fieldiq-border px-4 py-3 text-sm text-fieldiq-text">{sheet.remarks}</p>}
    </WlPanel>
  );
}

export function SheetReadout({ sheet }: { sheet: WlSheet }) {
  return (
    <>
      <WlPanel eyebrow="Crew" title="Crew" testId="wl-sheet-crew">
        {sheet.crew.length === 0 ? (
          <WlEmpty>No crew on this sheet.</WlEmpty>
        ) : (
          <ul className="divide-y divide-fieldiq-border">
            {sheet.crew.map((c) => (
              <li key={c.id} className="flex flex-wrap items-center justify-between gap-2 px-4 py-3" data-testid="wl-sheet-crew-row">
                <div>
                  <p className="font-semibold text-white">{c.userName || '—'}</p>
                  <p className="text-xs text-fieldiq-muted">{c.positionLabel ?? c.position}</p>
                </div>
                <p className="font-mono text-sm text-fieldiq-text">
                  {wlClockRange(c.on, c.off)} · {wlHours(c.hours)}
                  {c.travelHours ? ` · ${wlHours(c.travelHours)} travel` : ''}
                  {c.subsistence ? ' · subsistence' : ''}
                </p>
              </li>
            ))}
          </ul>
        )}
      </WlPanel>
      <WlPanel eyebrow="Equipment" title="Equipment and hours" testId="wl-sheet-equipment">
        {sheet.equipment.length === 0 ? (
          <WlEmpty>No equipment hours on this sheet.</WlEmpty>
        ) : (
          <ul className="divide-y divide-fieldiq-border">
            {sheet.equipment.map((e) => (
              <li key={e.id} className="flex flex-wrap items-center justify-between gap-2 px-4 py-3" data-testid="wl-sheet-equipment-row">
                <div>
                  <p className="font-semibold text-white">{e.unitName || '—'}</p>
                  <p className="text-xs text-fieldiq-muted">
                    {e.kind === 'rent_in' ? `Rent-in${e.vendorName ? ` · ${e.vendorName}` : ''}` : e.unitNumber || 'Owned'}
                  </p>
                </div>
                <p className="font-mono text-sm text-fieldiq-text">
                  {wlHours(e.hours)}
                  {e.kind === 'owned' ? ` · meter ${e.hourMeterEnd === null ? '—' : fmtWlNumber(e.hourMeterEnd)}` : ''}
                  {e.mileageDisplay ? ` · ${wlQty(e.mileageDisplay, 1)}` : ''}
                </p>
              </li>
            ))}
          </ul>
        )}
      </WlPanel>
    </>
  );
}

interface CrewDraft {
  userId: string;
  position: string;
  on: string;
  off: string;
  travel: string;
  subsistence: boolean;
  notes: string | null; // carried: crew is replaced wholesale on save
}
interface EqDraft {
  source: string; // `e:<equipmentId>` or `r:<rentInId>`
  hours: string;
  meter: string;
  mileage: string;
  mileageUnit: string;
  notes: string | null; // carried: equipment is replaced wholesale on save
}

const num = (s: string): number | null => (s.trim() === '' ? null : Number(s));

export interface SheetOption {
  id: string;
  name: string;
}

export function SheetEditor({
  sheet,
  catalog,
  users,
  equipment,
  rentIns,
  canEditCrew,
  pending,
  error,
  onSubmit,
}: {
  sheet: WlSheet;
  catalog: WlCatalog | null;
  /** People who can be named: loaded, else the sheet's own crew. */
  users: SheetOption[];
  equipment: SheetOption[];
  rentIns: SheetOption[];
  /** A field helper cannot change the crew (the API refuses position_cannot_edit_crew). */
  canEditCrew: boolean;
  pending: boolean;
  error: string | null;
  onSubmit: (body: WlSheetWrite) => void;
}) {
  const [start, setStart] = useState(sheet.startTime ?? '');
  const [end, setEnd] = useState(sheet.endTime ?? '');
  const [standby, setStandby] = useState(wlToInput(sheet.standbyHours));
  const [travel, setTravel] = useState(wlToInput(sheet.travelHours));
  const [remarks, setRemarks] = useState(sheet.remarks ?? '');
  const [crew, setCrew] = useState<CrewDraft[]>(
    sheet.crew.map((c) => ({ userId: c.userId, position: c.position, on: c.on ?? '', off: c.off ?? '', travel: wlToInput(c.travelHours), subsistence: !!c.subsistence, notes: c.notes ?? null })),
  );
  const [eq, setEq] = useState<EqDraft[]>(
    sheet.equipment.map((e) => ({
      source: e.kind === 'rent_in' ? `r:${e.rentInId}` : `e:${e.equipmentId}`,
      hours: wlToInput(e.hours),
      meter: wlToInput(e.hourMeterEnd),
      mileage: wlToInput(e.mileage?.value ?? null),
      mileageUnit: e.mileage?.unit ?? '',
      notes: e.notes ?? null,
    })),
  );
  const [localError, setLocalError] = useState<string | null>(null);
  const positions = catalog?.crewPositions?.length ? catalog.crewPositions : FALLBACK_POSITIONS;
  const crewOptions = mergeOptions(users, sheet.crew.map((c) => ({ id: c.userId, name: c.userName || '—' })));
  const eqOptions = mergeOptions(
    equipment,
    sheet.equipment.filter((e) => e.kind !== 'rent_in' && e.equipmentId).map((e) => ({ id: e.equipmentId as string, name: e.unitName || '—' })),
  );
  const rentOptions = mergeOptions(
    rentIns,
    sheet.equipment.filter((e) => e.kind === 'rent_in' && e.rentInId).map((e) => ({ id: e.rentInId as string, name: e.unitName || '—' })),
  );

  const submit = () => {
    setLocalError(null);
    for (const t of [start, end]) if (t && !WL_TIME_PATTERN.test(t)) return setLocalError('Times are 24-hour HH:MM, e.g. 07:00.');
    const crewOut: WlSheetCrewWrite[] = [];
    for (const c of crew) {
      if (!c.userId) return setLocalError('Choose who each crew row is, or remove the row.');
      for (const t of [c.on, c.off]) if (t && !WL_TIME_PATTERN.test(t)) return setLocalError('Crew on and off are 24-hour HH:MM.');
      crewOut.push({ user_id: c.userId, position: c.position, on: c.on || null, off: c.off || null, travel_hours: num(c.travel), subsistence: c.subsistence, notes: c.notes });
    }
    const eqOut: WlSheetEquipmentWrite[] = [];
    for (const e of eq) {
      if (!e.source) return setLocalError('Choose the unit on each equipment row, or remove the row.');
      const hours = num(e.hours);
      if (hours === null || !Number.isFinite(hours)) return setLocalError('Each equipment row needs its hours.');
      const row: WlSheetEquipmentWrite = e.source.startsWith('r:') ? { rent_in_id: e.source.slice(2), hours } : { equipment_id: e.source.slice(2), hours };
      if (!e.source.startsWith('r:')) row.hour_meter_end = num(e.meter);
      if (e.mileage.trim() !== '') {
        row.mileage = num(e.mileage);
        row.mileage_unit = e.mileageUnit || null;
      }
      row.notes = e.notes;
      eqOut.push(row);
    }
    const body: WlSheetWrite = {
      standby_hours: num(standby),
      travel_hours: num(travel),
      remarks: remarks.trim() === '' ? null : remarks.trim(),
      equipment: eqOut,
    };
    if (start) body.start_time = start;
    if (end) body.end_time = end;
    if (canEditCrew) body.crew = crewOut;
    onSubmit(body);
  };

  return (
    <div className="space-y-4" data-testid="wl-sheet-editor">
      <WlPanel eyebrow="Day sheet" title="Window and hours">
        <div className="grid gap-3 px-4 py-4 sm:grid-cols-2 lg:grid-cols-5">
          <WlField label="Start" hint="HH:MM">
            <input className={WL_INPUT} aria-label="Sheet start" value={start} onChange={(e) => setStart(e.target.value)} />
          </WlField>
          <WlField label="End" hint="HH:MM; past midnight runs into the next day">
            <input className={WL_INPUT} aria-label="Sheet end" value={end} onChange={(e) => setEnd(e.target.value)} />
          </WlField>
          <WlField label="Standby hours">
            <input inputMode="decimal" className={WL_INPUT} aria-label="Standby hours" value={standby} onChange={(e) => setStandby(e.target.value)} />
          </WlField>
          <WlField label="Travel hours">
            <input inputMode="decimal" className={WL_INPUT} aria-label="Travel hours" value={travel} onChange={(e) => setTravel(e.target.value)} />
          </WlField>
          <WlField label="Remarks">
            <input className={WL_INPUT} aria-label="Remarks" value={remarks} onChange={(e) => setRemarks(e.target.value)} />
          </WlField>
        </div>
      </WlPanel>

      <WlPanel
        eyebrow="Crew"
        title="Crew"
        actions={
          canEditCrew ? (
            <button type="button" className={WL_SMALL_BTN} onClick={() => setCrew((c) => [...c, { userId: '', position: 'helper', on: '', off: '', travel: '', subsistence: false, notes: null }])}>
              <Plus className="size-3.5" aria-hidden="true" />
              Add hand
            </button>
          ) : undefined
        }
      >
        {!canEditCrew && <p className="px-4 pt-3 text-sm text-fieldiq-muted">Only the supervisor or the operator on this sheet changes its crew.</p>}
        {crew.length === 0 ? (
          <WlEmpty>No crew yet. A sheet needs crew before it is submitted.</WlEmpty>
        ) : (
          <ul className="divide-y divide-fieldiq-border">
            {crew.map((c, i) => {
              const upd = (patch: Partial<CrewDraft>) => setCrew((all) => all.map((x, j) => (j === i ? { ...x, ...patch } : x)));
              return (
                <li key={i} className="grid gap-2 px-4 py-3 sm:grid-cols-2 lg:grid-cols-[2fr_1.2fr_0.8fr_0.8fr_0.8fr_auto_auto]" data-testid="wl-crew-edit-row">
                  <select className={WL_INPUT} aria-label={`Crew ${i + 1} person`} value={c.userId} disabled={!canEditCrew} onChange={(e) => upd({ userId: e.target.value })}>
                    <option value="">Choose a person</option>
                    {crewOptions.map((u) => (
                      <option key={u.id} value={u.id}>
                        {u.name}
                      </option>
                    ))}
                  </select>
                  <select className={WL_INPUT} aria-label={`Crew ${i + 1} position`} value={c.position} disabled={!canEditCrew} onChange={(e) => upd({ position: e.target.value })}>
                    {positions.map((p) => (
                      <option key={p.value} value={p.value}>
                        {p.label}
                      </option>
                    ))}
                  </select>
                  <input className={WL_INPUT} aria-label={`Crew ${i + 1} on`} placeholder="On" value={c.on} disabled={!canEditCrew} onChange={(e) => upd({ on: e.target.value })} />
                  <input className={WL_INPUT} aria-label={`Crew ${i + 1} off`} placeholder="Off" value={c.off} disabled={!canEditCrew} onChange={(e) => upd({ off: e.target.value })} />
                  <input inputMode="decimal" className={WL_INPUT} aria-label={`Crew ${i + 1} travel hours`} placeholder="Travel h" value={c.travel} disabled={!canEditCrew} onChange={(e) => upd({ travel: e.target.value })} />
                  <label className="inline-flex items-center gap-2 text-sm text-fieldiq-text">
                    <input type="checkbox" checked={c.subsistence} disabled={!canEditCrew} onChange={(e) => upd({ subsistence: e.target.checked })} />
                    Subsistence
                  </label>
                  {canEditCrew && (
                    <button type="button" className={WL_SMALL_BTN} aria-label={`Remove crew ${i + 1}`} onClick={() => setCrew((all) => all.filter((_, j) => j !== i))}>
                      <Trash2 className="size-3.5" aria-hidden="true" />
                    </button>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </WlPanel>

      <WlPanel
        eyebrow="Equipment"
        title="Equipment and hours"
        actions={
          <button type="button" className={WL_SMALL_BTN} onClick={() => setEq((e) => [...e, { source: '', hours: '', meter: '', mileage: '', mileageUnit: '', notes: null }])}>
            <Plus className="size-3.5" aria-hidden="true" />
            Add unit
          </button>
        }
      >
        {eq.length === 0 ? (
          <WlEmpty>No equipment hours yet.</WlEmpty>
        ) : (
          <ul className="divide-y divide-fieldiq-border">
            {eq.map((e, i) => {
              const upd = (patch: Partial<EqDraft>) => setEq((all) => all.map((x, j) => (j === i ? { ...x, ...patch } : x)));
              const rent = e.source.startsWith('r:');
              return (
                <li key={i} className="grid gap-2 px-4 py-3 sm:grid-cols-2 lg:grid-cols-[2fr_0.8fr_1fr_0.8fr_0.8fr_auto]" data-testid="wl-eq-edit-row">
                  <select className={WL_INPUT} aria-label={`Equipment ${i + 1}`} value={e.source} onChange={(ev) => upd({ source: ev.target.value })}>
                    <option value="">Choose a unit</option>
                    {eqOptions.length > 0 && (
                      <optgroup label="Owned">
                        {eqOptions.map((o) => (
                          <option key={o.id} value={`e:${o.id}`}>
                            {o.name}
                          </option>
                        ))}
                      </optgroup>
                    )}
                    {rentOptions.length > 0 && (
                      <optgroup label="Rented in">
                        {rentOptions.map((o) => (
                          <option key={o.id} value={`r:${o.id}`}>
                            {o.name}
                          </option>
                        ))}
                      </optgroup>
                    )}
                  </select>
                  <input inputMode="decimal" className={WL_INPUT} aria-label={`Equipment ${i + 1} hours`} placeholder="Hours" value={e.hours} onChange={(ev) => upd({ hours: ev.target.value })} />
                  <input
                    inputMode="decimal"
                    className={WL_INPUT}
                    aria-label={`Equipment ${i + 1} hour meter end`}
                    placeholder={rent ? 'No meter on a rent-in' : 'Hour meter end'}
                    value={rent ? '' : e.meter}
                    disabled={rent}
                    onChange={(ev) => upd({ meter: ev.target.value })}
                  />
                  <input inputMode="decimal" className={WL_INPUT} aria-label={`Equipment ${i + 1} mileage`} placeholder="Mileage" value={e.mileage} onChange={(ev) => upd({ mileage: ev.target.value })} />
                  <select className={WL_INPUT} aria-label={`Equipment ${i + 1} mileage unit`} value={e.mileageUnit} onChange={(ev) => upd({ mileageUnit: ev.target.value })}>
                    <option value="">Unit</option>
                    {(catalog?.distanceUnits ?? ['km', 'mi']).map((u) => (
                      <option key={u} value={u}>
                        {u}
                      </option>
                    ))}
                  </select>
                  <button type="button" className={WL_SMALL_BTN} aria-label={`Remove equipment ${i + 1}`} onClick={() => setEq((all) => all.filter((_, j) => j !== i))}>
                    <Trash2 className="size-3.5" aria-hidden="true" />
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </WlPanel>

      <WlFormError message={localError ?? error} />
      <div className="flex justify-end">
        <button type="button" className={WL_PRIMARY} disabled={pending} onClick={submit} data-testid="wl-sheet-save">
          {pending ? 'Saving…' : 'Save sheet'}
        </button>
      </div>
    </div>
  );
}

function mergeOptions(a: SheetOption[], b: SheetOption[]): SheetOption[] {
  const seen = new Set<string>();
  const out: SheetOption[] = [];
  for (const o of [...a, ...b]) {
    if (seen.has(o.id)) continue;
    seen.add(o.id);
    out.push(o);
  }
  return out;
}

export function SheetRuns({
  sheet,
  runPath,
  newRunPath,
  canAdd,
}: {
  sheet: WlSheet;
  runPath: (id: string) => string;
  newRunPath: string;
  canAdd: boolean;
}) {
  return (
    <WlPanel
      eyebrow="Runs"
      title="Runs"
      testId="wl-sheet-runs"
      actions={
        canAdd ? (
          <Link to={newRunPath} className={WL_SMALL_BTN} data-testid="wl-add-run">
            <Plus className="size-3.5" aria-hidden="true" />
            Add run
          </Link>
        ) : undefined
      }
    >
      {sheet.runs.length === 0 ? (
        <WlEmpty>No run on this sheet yet.</WlEmpty>
      ) : (
        <ul className="divide-y divide-fieldiq-border">
          {sheet.runs.map((r) => (
            <li key={r.id}>
              <Link to={runPath(r.id)} className="flex items-center gap-3 px-4 py-3 hover:bg-fieldiq-dark/40" data-testid="wl-sheet-run">
                <div className="min-w-0 flex-1">
                  <p className="font-semibold text-white">
                    Run {r.runNumber} · {r.runKindLabel ?? r.runKind}
                  </p>
                  <p className="mt-0.5 font-mono text-xs text-fieldiq-text">
                    {wlClockRange(r.rigUpStart, r.rigDown)} · max {wlQty(r.maxDepthDisplay, 1)} · footage {wlQty(r.footage, 1)}
                    {r.resultLabel ? ` · ${r.resultLabel}` : ''}
                  </p>
                  {r.warnings.length > 0 && <p className="mt-0.5 text-xs text-fi-amber">{r.warnings.length} warning{r.warnings.length === 1 ? '' : 's'}</p>}
                </div>
                <ChevronRight className="size-5 shrink-0 text-fieldiq-muted" aria-hidden="true" />
              </Link>
            </li>
          ))}
        </ul>
      )}
    </WlPanel>
  );
}
